import React, { useContext, useState, useCallback, useEffect } from 'react';
import { useNodesState, useEdgesState, Node, Edge } from '@xyflow/react';
import {
  CustomDashboardContextTypes,
  CustomDashboardProviderPropsTypes,
  DrawerTypeTypes,
} from './types';
import { DashboardService } from 'src/services/dashboard/dashboard.service';
import { CreateDashboardPayloadType } from 'src/services/dashboard/types';

const CustomerContext = React.createContext<CustomDashboardContextTypes>(
  {} as CustomDashboardContextTypes
);

export const useCustomDashboardContext = () => {
  const context = useContext(CustomerContext);

  return context;
};

const initialEdges: any[] = [];

export const CustomDashboardProvider = ({
  children,
  mode,
  initialNodes = [],
  initialTitle,
}: CustomDashboardProviderPropsTypes) => {
  const [title, setTitle] = useState<string>('Dashboard');

  const [isDrawerOpened, setIsDrawerOpened] = useState<boolean>(false);
  //Selected drawer type
  const [drawerType, setDrawerType] = useState<DrawerTypeTypes>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>(mode === 'edit' ? initialNodes : []);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>(initialEdges);

  const [hasHydrated, setHasHydrated] = useState(false);

  useEffect(() => {
    if (mode === 'edit' && !hasHydrated && initialNodes.length > 0) {
      setNodes(
        initialNodes.map((item) => ({
          ...item,
          selected: false,
          dragging: false,
        }))
      );
      setHasHydrated(true);
    }
  }, [mode, initialNodes, hasHydrated, setNodes]);

  useEffect(() => {
    setTitle(initialTitle ? initialTitle : 'Dashboard');
  }, [initialTitle]);

  const selectedNode = nodes.find((n) => n.id === selectedNodeId) ?? null;

  const selectNode = useCallback((node: Node) => {
    setSelectedNodeId(node.id);
    setIsDrawerOpened(true);
    setDrawerType(node?.data?.drawerType as DrawerTypeTypes);
  }, []);

  const closeDrawer = useCallback(() => {
    setSelectedNodeId(null);
    setIsDrawerOpened(false);
  }, []);

  const resetToInitialNodes = useCallback(() => {
    setNodes(
      initialNodes.map((item) => ({
        ...item,
        selected: false,
        dragging: false,
      }))
    );
  }, [initialNodes, setNodes]);

  const updateNodeData = useCallback(
    (nodeId: string, newData: any) => {
      setNodes((nds) =>
        nds.map((node) =>
          node.id === nodeId ? { ...node, data: { ...node.data, ...newData } } : node
        )
      );
    },
    [setNodes]
  );

  const deleteNode = useCallback(
    (nodeId: string) => {
      setNodes((prevNodes: Node[]) => prevNodes.filter((node) => node.id !== nodeId));
      setIsDrawerOpened(false);
    },
    [setNodes]
  );

  const createDashboardHandler = useCallback(
    async ({ customerId, data }: { customerId: string; data: CreateDashboardPayloadType }) => {
      try {
        const response = await DashboardService.createDashboard({
          customerId: customerId,
          data: data,
        });
        return response;
      } catch (err) {
        throw err;
      }
    },
    []
  );

  const editDashboardHandler = useCallback(
    async ({
      customerId,
      data,
      dashboardId,
    }: {
      customerId: string;
      data: CreateDashboardPayloadType;
      dashboardId: string;
    }) => {
      try {
        const response = await DashboardService.editDashboard({
          customerId: customerId,
          data: data,
          dashboardId: dashboardId,
        });
        return response;
      } catch (err) {
        throw err;
      }
    },
    []
  );

  const onConfirmTitleHandler = useCallback((title: string) => {
    setTitle(title);
  }, []);

  return (
    <CustomerContext.Provider
      value={{
        isDrawerOpened,
        drawerType,
        selectedNode,
        closeDrawer,
        nodes,
        edges,
        setNodes,
        setEdges,
        deleteNode,
        onEdgesChange,
        onNodesChange,
        selectNode,
        updateNodeData,
        setDrawerType,
        createDashboardHandler,
        editDashboardHandler,
        onConfirmTitleHandler,
        resetToInitialNodes,
        title,
      }}
    >
      {children}
    </CustomerContext.Provider>
  );
};
